import { Link } from '@remix-run/react';
import { User } from '@supabase/supabase-js';
import { FC, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { MoonIcon } from '~/icons/moon';
import { SunIcon } from '~/icons/sun';
import { SITE_NAME } from '~/utils/constants';

type Props = {
    isLive: boolean;
    user: User | null;
};

export const Navbar: FC<Props> = ({ isLive, user }: Props) => {
    const [isHamburgerOpen, setIsHamburgerOpen] = useState<boolean>();

    const siteHomeButtonText = SITE_NAME.match(/[A-Z][a-z]+/g)?.map(
        (value, index) => {
            if (index !== 1)
                return (
                    <p className="pb-1" key={`${index}-home-button`}>
                        {value}
                    </p>
                );

            return (
                <p
                    className="text-gradient pb-1 font-bold"
                    key={`${index}-home-button`}
                >
                    {value}
                </p>
            );
        }
    );

    return (
        <div className="navbar sticky top-0 z-10 flex flex-col items-start justify-between bg-base-300 drop-shadow-sm md:flex-row md:items-center">
            <div>
                {isMobile && isLive ? (
                    <button
                        className="btn btn-ghost"
                        onClick={() => setIsHamburgerOpen(!isHamburgerOpen)}
                    >
                        {siteHomeButtonText}
                    </button>
                ) : (
                    <Link
                        className="btn btn-ghost text-xl md:text-3xl"
                        prefetch="intent"
                        to="/"
                    >
                        {siteHomeButtonText}
                    </Link>
                )}
            </div>

            {isHamburgerOpen && (
                <>
                    <div className="flex w-full flex-1">
                        <ul className="menu menu-vertical menu-md w-full rounded-box bg-base-200">
                            {user !== null && (
                                <>
                                    <li>
                                        <a href="/dashboard">Dashboard</a>
                                    </li>
                                    <li>
                                        <a href="/skills">My Skills</a>
                                    </li>
                                    <li>
                                        <a href="/experience">My Experience</a>
                                    </li>
                                    <li>
                                        <a href="/education">My Education</a>
                                    </li>
                                </>
                            )}
                            <li>
                                <a href="/about">About</a>
                            </li>
                            <li>
                                <a href="/pricing">Pricing</a>
                            </li>
                            <li>
                                <a href="/faq">FAQ</a>
                            </li>
                            <li>
                                <a href="/contact-us">Contact Us</a>
                            </li>
                        </ul>
                    </div>
                    {/* TODO fix this below to add light mode to mobile */}
                    {/* <div className="space-x-1 pr-5">
                        <button
                            className="btn btn-circle btn-ghost"
                            data-set-theme="light"
                        >
                            <SunIcon />
                        </button>
                        <p className="text-xl font-bold">/</p>
                        <button
                            className="btn btn-circle btn-ghost"
                            data-set-theme="dark"
                        >
                            <MoonIcon />
                        </button>
                    </div> */}
                    <div className="flex-none">
                        <div className="dropdown dropdown-end">
                            {user ? (
                                <>
                                    <div
                                        className="avatar btn btn-ghost"
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {user.email}
                                    </div>
                                    <ul className="menu dropdown-content menu-sm z-[999] mt-3 w-52 rounded-box bg-base-100 p-2 shadow">
                                        <li>
                                            <a href="/settings">Settings</a>
                                        </li>
                                        <li>
                                            <a href="/logout">Logout</a>
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <a className="btn btn-ghost" href="/login">
                                    Log In / Sign Up
                                </a>
                            )}
                        </div>
                    </div>
                </>
            )}

            {isLive && !isMobile && (
                <>
                    <div className="flex w-full flex-1 md:w-auto">
                        <ul className="menu menu-vertical menu-md w-full rounded-box bg-base-200 md:menu-horizontal md:w-auto">
                            {user !== null && (
                                <>
                                    <li>
                                        <a href="/dashboard">Dashboard</a>
                                    </li>
                                    <li>
                                        <a href="/skills">My Skills</a>
                                    </li>
                                    <li>
                                        <a href="/experience">My Experience</a>
                                    </li>
                                    <li>
                                        <a href="/education">My Education</a>
                                    </li>
                                </>
                            )}
                            <li>
                                <a href="/about">About</a>
                            </li>
                            <li>
                                <a href="/pricing">Pricing</a>
                            </li>
                            <li>
                                <a href="/faq">FAQ</a>
                            </li>
                            <li>
                                <a href="/contact-us">Contact Us</a>
                            </li>
                        </ul>
                    </div>
                    <div className="space-x-1 pr-5 md:space-x-2">
                        <button
                            className="btn btn-circle btn-ghost"
                            data-set-theme="light"
                        >
                            <SunIcon />
                        </button>
                        <p className="text-xl font-bold md:text-2xl">/</p>
                        <button
                            className="btn btn-circle btn-ghost"
                            data-set-theme="dark"
                        >
                            <MoonIcon />
                        </button>
                    </div>
                    <div className="flex-none">
                        <div className="dropdown dropdown-end">
                            {user ? (
                                <>
                                    <div
                                        className="avatar btn btn-ghost"
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {user.email}
                                    </div>
                                    <ul className="menu dropdown-content menu-sm z-[999] mt-3 w-52 rounded-box bg-base-100 p-2 shadow">
                                        <li>
                                            <a href="/settings">Settings</a>
                                        </li>
                                        <li>
                                            <a href="/logout">Logout</a>
                                        </li>
                                    </ul>
                                </>
                            ) : (
                                <a className="btn btn-ghost" href="/login">
                                    Log In / Sign Up
                                </a>
                            )}
                        </div>
                    </div>
                </>
            )}
            {!isLive && !isMobile && (
                <div className="ml-auto flex">
                    <button
                        className="btn btn-circle btn-ghost"
                        data-set-theme="light"
                    >
                        <SunIcon />
                    </button>
                    <p className="text-2xl font-bold">/</p>
                    <button
                        className="btn btn-circle btn-ghost"
                        data-set-theme="dark"
                    >
                        <MoonIcon />
                    </button>
                </div>
            )}
        </div>
    );
};
